import { memo, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { SDropdownMenu } from '@setvi/shared/components';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import {
  DeleteIconV2,
  EditIcon,
  RefreshIcon
} from '@setvi/shared/components/sicons';
import { COLUMN_WIDTHS } from '../column-widths';
import { ProductInfo } from './product-info';
import { QuantityColumn, SellColumn, CostColumn, GpColumn } from './columns';
import { FirstRowBox, RowContainer, SecondRowBox } from './styled';
import { useOptionRowStyles } from './option-row-styles';
import {
  useFieldLock,
  useItemAnyFieldLock,
  usePricingLock
} from '../../../pages/details/realtime/use-field-locks';
import {
  HIGHLIGHT_BOX_SHADOW,
  QuoteRowPresenceBadge,
  useQuoteRowPresence
} from '../../../pages/details/realtime/quote-row-presence';

/**
 * Handlers passed from parent to avoid context subscriptions in each row
 */
export interface OptionRowHandlers {
  onQuantityChange: (productId: string, value: number) => void;
  onSellChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginPercentageChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onEditOption: (product: QuoteProductDTO) => void;
  onRemoveOption: (productId: string) => void;
  onRefreshPrice: (productId: string, sku: string) => void;
}

interface OptionRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  isSectionChild?: boolean;
  onProductClick?: (product: QuoteProductDTO) => void;
  isLastChild?: boolean;
  isQuoteEditable: boolean;
  handlers: OptionRowHandlers;
  isRfqQuote?: boolean;
}

/**
 * OptionRow - Lightweight presentational component for option rows
 *
 * This component does NOT subscribe to any contexts to minimize memory usage.
 * All handlers and state are passed down from the parent ProductRow.
 */
export const OptionRow = memo(
  ({
    product,
    searchQuery,
    isSectionChild,
    onProductClick,
    isLastChild = false,
    isQuoteEditable,
    handlers,
    isRfqQuote
  }: OptionRowProps) => {
    const classes = useOptionRowStyles();
    const productId = product?.id || product?._id;
    const notEditableTooltip = 'Quote is not editable';
    // Presence: show who's on THIS option row and broadcast it on hover.
    const { members, rowProps, isHighlighted } = useQuoteRowPresence(productId);
    // Edit Option Details edits the option's note + label; lock it when another
    // collaborator already has that dialog open for this option.
    const noteLock = useFieldLock(productId, 'note');
    const labelLock = useFieldLock(productId, 'label');
    const editDetailsLockedByOther =
      noteLock.lockedByOther || labelLock.lockedByOther;
    const editDetailsHolder = noteLock.holder || labelLock.holder;
    // Pricing locks: each pricing input locks independently (avatar only on the
    // edited input) but disables the group. Refresh Price is blocked while any
    // pricing input is held.
    const pricing = usePricingLock(productId);
    // Block Remove while another collaborator is working on this option in ANY
    // way (pricing, note/label, or an open Remove dialog).
    const optionBusy = useItemAnyFieldLock(productId);

    const menuItems = useMemo(
      () =>
        !isQuoteEditable
          ? []
          : [
              {
                color: '#333D47',
                icon: <RefreshIcon fontSize="small" />,
                text: 'Refresh Price',
                disabled:
                  product?.isFreeHand || !product?.sku || pricing.lockedByOther,
                tooltipText: pricing.lockedByOther
                  ? `${pricing.holder?.name || 'Another user'} is editing the price`
                  : undefined,
                onClick: () => handlers.onRefreshPrice(productId, product?.sku)
              },
              {
                color: '#333D47',
                icon: <EditIcon fontSize="small" />,
                text: 'Edit Option Details',
                disabled: editDetailsLockedByOther,
                tooltipText: editDetailsLockedByOther
                  ? `${editDetailsHolder?.name || 'Another user'} is editing this option`
                  : undefined,
                onClick: () => handlers.onEditOption(product)
              },
              {
                icon: <DeleteIconV2 fontSize="small" />,
                color: 'danger',
                text: 'Remove Option',
                // Block removal while another collaborator works on this option.
                disabled: optionBusy.lockedByOther,
                tooltipText: optionBusy.lockedByOther
                  ? `${optionBusy.holder?.name || 'Another user'} is editing this option`
                  : undefined,
                onClick: () => handlers.onRemoveOption(productId)
              }
            ],
      [
        isQuoteEditable,
        handlers,
        product,
        productId,
        editDetailsLockedByOther,
        editDetailsHolder,
        optionBusy.lockedByOther,
        optionBusy.holder,
        pricing.lockedByOther,
        pricing.holder
      ]
    );

    return (
      <Box className={classes.optionRowWrapper} {...rowProps}>
        <Box className={classes.connectingLineContainer}>
          <Box
            className={
              isLastChild ? classes.verticalLineLastChild : classes.verticalLine
            }
          />
          <Box className={classes.cornerPiece} />
          <Box className={classes.horizontalConnector} />
        </Box>
        <Box
          className={classes.rowContainer}
          style={{
            transition: 'box-shadow 0.4s ease',
            boxShadow: isHighlighted ? HIGHLIGHT_BOX_SHADOW : undefined
          }}>
          <QuoteRowPresenceBadge members={members} />
          <Box className={classes.productContainer}>
            <Box className={classes.productInfo}>
              <ProductInfo
                product={product}
                searchQuery={searchQuery}
                onClick={
                  onProductClick ? () => onProductClick(product) : undefined
                }
                badgeText={product?.labelType}
                isRfqQuote={isRfqQuote}
              />
            </Box>

            <Box className={classes.qtyColumn}>
              <QuantityColumn
                product={product}
                productId={productId}
                onQuantityChange={handlers.onQuantityChange}
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.unitPriceColumn}>
              <SellColumn
                product={product}
                productId={productId}
                onSellChange={handlers.onSellChange}
                sellLock={pricing.sell}
                pricingLockedByOther={pricing.lockedByOther}
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.gpColumn}>
              <GpColumn
                product={product}
                productId={productId}
                onProfitMarginChange={handlers.onProfitMarginChange}
                onProfitMarginPercentageChange={
                  handlers.onProfitMarginPercentageChange
                }
                gpLock={pricing.gp}
                gpPercentLock={pricing.gpPercent}
                pricingLockedByOther={pricing.lockedByOther}
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.totalPriceColumn}>
              <CostColumn product={product} />
            </Box>

            <Box
              className={classes.actionColumn}
              style={
                isSectionChild
                  ? { width: `calc(${COLUMN_WIDTHS.action} - 12px)` }
                  : undefined
              }>
              <RowContainer>
                <FirstRowBox>
                  <SDropdownMenu
                    newDesign
                    disabled={!isQuoteEditable}
                    disabledTooltip={notEditableTooltip}
                    menuItems={menuItems}
                  />
                </FirstRowBox>
                <SecondRowBox hasNote={!!product?.note} />
              </RowContainer>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
